import Media from 'react-media';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';

import { Box, Grid, Text, useTheme } from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import Carousel, { Slides, Slide } from '@components/Carousel';
import { Sentry } from '@/components/Sentry';

import { useCompanyNarratives } from '@/pages/CompanyReport/components/CompanyNarratives/hooks/useCompanyNarratives';
import { Section } from '@/pages/CompanyReport/components/Overview/components/Section';
import { SubSectionHeader } from '@/pages/CompanyReport/components/Overview/components/SubSectionHeader';

import { StyledHeaderGrid } from './styled';

const NarrativeCard = loadable(
  () => import('@/components/NarrativeCard/PopulatedState'),
  { resolveComponent: (c) => c.PopulatedState }
);

const CompanyNarratives = ({ canonicalUrl }: { canonicalUrl: string }) => {
  const theme = useTheme();
  const { isMobile, isDesktop } = useUserAgent();
  const { data, status } = useCompanyNarratives({ canonicalUrl });
  const narratives = data?.NarrativesByCompanyCanonical;
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'narrative',
  });

  if (status === 'error' || status === 'pending') {
    return null;
  }

  if (typeof narratives === 'undefined' || narratives.length === 0) {
    return null;
  }

  const handleOnClick = (narrative: (typeof narratives)[number]) => {
    return trackEvent({
      action: 'click',
      modifier: 'view',
      narrativeId: narrative.id,
      companyId: narrative?.company?.id,
      uniqueSymbol: narrative?.company?.tickerSymbol,
    });
  };

  return (
    <Track>
      <Section data-cy-id="company-narratives">
        <StyledHeaderGrid as="header">
          <SubSectionHeader data-cy-id="company-narratives-heading">
            Community Narratives
          </SubSectionHeader>
        </StyledHeaderGrid>
        <Box marginBottom="x2">
          <Text typography="xSmall" data-cy-id="company-narratives-by-line">
            Narratives bring a range of perspectives from our community.
          </Text>
        </Box>
        <Sentry
          sentryCapture={{ team: 'narwhals', extra: { canonicalUrl } }}
          globalDisabled
        >
          <Media
            queries={{
              mobile: { maxWidth: theme.smMaxRaw },
              desktop: { minWidth: theme.lgMinRaw },
            }}
            defaultMatches={{
              mobile: isMobile,
              desktop: isDesktop,
            }}
          >
            {(matches) => {
              if (matches.mobile) {
                return (
                  <Carousel totalSlides={4} align="start">
                    <Slides>
                      {narratives.slice(0, 4).map((narrative) => (
                        <Slide
                          key={narrative.id}
                          style={{ margin: `0 ${theme.x1}px` }}
                        >
                          <NarrativeCard
                            narrative={narrative}
                            onClick={() => handleOnClick(narrative)}
                          />
                        </Slide>
                      ))}
                    </Slides>
                  </Carousel>
                );
              }

              return (
                <Grid gap={`${theme.x2}px`} templateColumns="repeat(2, 1fr)">
                  {narratives.slice(0, 4).map((narrative) => (
                    <NarrativeCard
                      key={narrative.id}
                      narrative={narrative}
                      onClick={() => handleOnClick(narrative)}
                    />
                  ))}
                </Grid>
              );
            }}
          </Media>
        </Sentry>
      </Section>
    </Track>
  );
};

export default CompanyNarratives;
